// In development, a stale service worker (registered by a previous prod
// build or an earlier session) can intercept requests for Vite's dev chunks
// and serve stale JS — which causes "Cannot read properties of null" React
// hook errors because the stale chunks reference a different React copy.
// This module unregisters every SW and wipes SW caches when running in DEV,
// and exposes a guarded register helper so push-notification code only
// registers the SW in production.

const isDev = import.meta.env?.DEV === true || import.meta.env?.MODE === 'development';

export async function clearDevServiceWorkers() {
  if (!isDev) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  } catch (e) {
    // ignore
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) {
    // ignore
  }
}

// Only call this in production. In dev it is a no-op.
export async function registerPushServiceWorker() {
  if (isDev) return null;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  const swUrl = window.location.origin + '/sw.js';

  // Unregister any old SW registered at /functions/sw (stale, cached version)
  try {
    const existing = await navigator.serviceWorker.getRegistrations();
    await Promise.all(existing.map((r) => {
      if (r.active && r.active.scriptURL.includes('/functions/sw')) {
        return r.unregister();
      }
      return null;
    }));
  } catch (e) { /* ignore */ }

  const registration = await navigator.serviceWorker.register(swUrl, { scope: '/' });
  // Force-check for SW updates immediately so the new skipWaiting/renotify
  // version replaces the old one without waiting for the 24h browser timer.
  try { await registration.update(); } catch (e) { /* ignore */ }
  // If a new SW is waiting, listen for it and activate it immediately
  if (registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => {});
  await navigator.serviceWorker.ready;
  return registration;
}

// Force-update the service worker on every page load (production only).
// This ensures the latest SW with renotify:true and unique tags is active.
export async function forceServiceWorkerUpdate() {
  if (isDev) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const reg of registrations) {
      // Unregister old SW at /functions/sw, update others
      if (reg.active && reg.active.scriptURL.includes('/functions/sw')) {
        await reg.unregister();
      } else {
        await reg.update();
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }
    }
  } catch (e) { /* ignore */ }
}

// Run cleanup immediately on module load in dev.
if (isDev && typeof window !== 'undefined') {
  clearDevServiceWorkers();
}